// MODIFIED: Upgraded copy from descriptive text to high-converting marketing benefits.
// Emphasized zero battery drain, smart reminders (to prevent false alarms), and automatic privacy wiping.

import { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { MapPin, Users, Timer, ShieldCheck, Bell, CheckCircle2 } from 'lucide-react';
import { useIsMobile } from '../hooks/use-mobile';

const steps = [
  { 
    icon: MapPin, 
    title: "Map Your Trip", 
    // MODIFIED: Made it actionable and included custom notes
    description: "Enter your destination and add any private notes or instructions for your trip." 
  },
  { 
    icon: Users, 
    title: "Select Your Circle", 
    // MODIFIED: Re-enforced the "Trust Circles" concept from previous sections
    description: "Choose exactly who gets notified from your trusted family or friends." 
  },
  { 
    icon: Timer, 
    title: "Set Your Deadline", 
    // MODIFIED: Highlighted cloud reliability as a subtle selling point
    description: "Define your expected safe return time. The countdown runs securely on our cloud." 
  },
  { 
    icon: ShieldCheck, 
    title: "Silent Background Guard", 
    // MODIFIED: Addressed user fears directly: No battery drain, no live tracking
    description: "Go about your day. The app sleeps quietly with zero battery drain or GPS surveillance." 
  },
  { 
    icon: Bell, 
    title: "Smart Local Reminders", 
    // MODIFIED: Aligned with FAQ (1h & 10m warnings) and relieved fear of false alarms
    description: "Get gentle nudges before time expires. Running late? Quickly snooze to prevent false alarms." 
  },
  { 
    icon: CheckCircle2, 
    title: "Check In or Auto-Alert", 
    // MODIFIED: Highlighted the "Instant Wipe" privacy feature upon safe arrival
    description: "Tap 'Arrived Safely' to instantly wipe your trip data, or our servers automatically alert your circle." 
  },
];

export default function HowItWorks() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const [hoveredStep, setHoveredStep] = useState<number | null>(null);
  const isMobile = useIsMobile();

  return (
    <section id="how-it-works" ref={ref} className="relative py-32 md:py-40 bg-slate-50 overflow-hidden">
      <div className="container mx-auto px-6 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: isMobile ? 0 : 0.8 }}
          className="text-center mb-20"
        >
          {/* MODIFIED: More dynamic marketing tag */}
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600 mb-4">
            Zero-Friction Safety
          </span>
          {/* MODIFIED: Stronger action-oriented headline */}
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 max-w-2xl mx-auto leading-tight">
            Safety on autopilot in <span className="text-emerald-600">6 simple steps</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
          {steps.map((step, index) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: isMobile ? 0 : 0.6, delay: isMobile ? 0 : index * 0.1 }}
              onMouseEnter={() => setHoveredStep(index)}
              onMouseLeave={() => setHoveredStep(null)}
              className="relative group"
            >
              {index < steps.length - 1 && index % 3 !== 2 && (
                <div className="hidden lg:block absolute top-8 left-full w-full h-[2px]">
                  <motion.div
                    className="h-full"
                    animate={{
                      background: hoveredStep === index || hoveredStep === index + 1
                        ? 'linear-gradient(90deg, rgba(16,185,129,0.4), rgba(16,185,129,0.1))'
                        : 'linear-gradient(90deg, rgba(16,185,129,0.1), transparent)'
                    }}
                  />
                </div>
              )}

              <div className="relative">
                <div className="absolute -top-4 -left-2 text-7xl font-bold text-slate-100 select-none">
                  {String(index + 1).padStart(2, '0')}
                </div>

                <div className="relative pt-6">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 transition-all duration-300 ${
                    hoveredStep === index
                      ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/25'
                      : 'bg-white text-emerald-600 shadow-sm'
                  }`}>
                    <step.icon className="w-6 h-6" />
                  </div>

                  <h3 className="text-lg font-bold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{step.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}